import { useState } from 'react';
import { MapPin, Save, Loader2 } from 'lucide-react';
import { updateLocation } from '@/lib/api';
import sharedStyles from './ResidentChargersCard.module.css';
import styles from './LocationGooglePlaceIdCard.module.css';

/**
 * Google Maps Place ID — admin pastes the place_id for the
 * Location so Phase 3's post-session review CTA has a deep-link
 * target. Optional field; Locations without a place_id simply
 * don't surface the CTA on the PWA.
 *
 * Save model
 * ──────────
 * Explicit Save button (not auto-save on blur) — admin's likely
 * to paste the value with stray whitespace or tweak before
 * commit, and the auto-save pattern would write+audit on every
 * intermediate state. The button only enables when the local
 * draft differs from the prop value, so a pristine view shows
 * the saved state without an "unsaved" affordance.
 *
 * Optimistic UI: the click flips the button to a saving spinner
 * + disables the input. On success, parent's onChange fires +
 * the inline savedAt confirms. On error, the error message
 * surfaces inline + draft state is preserved so the admin can
 * edit + retry.
 *
 * Visual chrome reuses ResidentChargersCard.module.css for the
 * card / title / alert classes so the GMap card and the
 * Resident-Managed card sit visually adjacent on
 * LocationDetailPage as a uniform pair. Input-specific styles
 * live in this card's own module.
 */
interface LocationGooglePlaceIdCardProps {
  locationId: string;
  /** Current value, fed by parent's LocationDetail state.
   *  Empty/undefined renders the empty input ready for paste. */
  value: string | null | undefined;
  /** Notify the parent of a successful save so the cached
   *  LocationDetail stays in sync. */
  onChange?: (next: string) => void;
}

const SAMPLE_PLACE_ID = 'ChIJN1t_tDeuEmsRUsoyG83frY4';

export function LocationGooglePlaceIdCard({
  locationId, value, onChange,
}: LocationGooglePlaceIdCardProps) {
  // Local draft — admin can edit freely without the parent's
  // LocationDetail state interfering. Initialized from props;
  // resyncs to props on a successful save (via parent's
  // onChange → re-render with new value) and on prop change
  // from elsewhere when no save is in flight.
  const initial = (value ?? '');
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-sync from props when nothing's in flight. Catches the
  // case where the parent refetches the LocationDetail and the
  // value changes externally; without this, the draft would
  // stay stuck on the old value.
  if (!saving && draft !== initial && savedAt === null && error === '') {
    setDraft(initial);
  }

  const dirty = draft.trim() !== initial.trim();

  const handleSave = async (): Promise<void> => {
    if (saving || !dirty) return;
    const next = draft.trim();
    setSaving(true);
    setError('');
    setSavedAt(null);
    try {
      await updateLocation(locationId, {
        // Send the trimmed value. Empty string clears the
        // field — the API audit captures the previous → ''
        // transition explicitly.
        google_place_id: next,
      });
      setSavedAt(Date.now());
      onChange?.(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('LocationGooglePlaceIdCard update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={sharedStyles.card}>
      <div className={sharedStyles.cardTitleRow}>
        <h2 className={sharedStyles.cardTitle}>
          <MapPin size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Google Maps Place ID
        </h2>
      </div>

      {error && <div className={sharedStyles.alertErr}>{error}</div>}
      {savedAt && !error && (
        <div className={sharedStyles.alertOk}>Saved.</div>
      )}

      <div className={styles.fieldRow}>
        <input
          type="text"
          className={styles.input}
          placeholder={SAMPLE_PLACE_ID}
          value={draft}
          disabled={saving}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          onChange={(e) => {
            setDraft(e.target.value);
            // Editing after a save clears the inline confirmation
            // so the next save's "Saved." message reads as fresh.
            if (savedAt !== null) setSavedAt(null);
            if (error !== '') setError('');
          }}
        />
        <button
          type="button"
          className={styles.saveBtn}
          disabled={!dirty || saving}
          onClick={() => { void handleSave(); }}
        >
          {saving ? (
            <>
              <Loader2 size={13} className={styles.spin} /> Saving…
            </>
          ) : (
            <>
              <Save size={13} /> Save
            </>
          )}
        </button>
      </div>

      <p className={styles.hint}>
        Used to deep-link drivers to leave a Google Maps review
        for this location after a charging session. Find your
        place ID by searching your business on Google Maps,
        clicking <strong>Share</strong>, and copying the value
        from the embed URL after <code>place_id=</code>. Or use
        Google&rsquo;s{' '}
        <a
          href="https://developers.google.com/maps/documentation/places/web-service/place-id"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.hintLink}
        >
          Place ID Finder
        </a>
        .
      </p>
    </section>
  );
}
